/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { ContactFormCommunicationChannel as Channel } from 'graphql-ts-types';
import type { WidthProps } from 'styled-system';
import { width } from 'styled-system';

import Alert from 'common/components/Alert';
import { Button } from 'common/components/Button';
import FormSubmitButton from 'common/components/Form/FormSubmitButton';
import { BodyRegularBR1, HeadlineH5, HeadlineH8 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useScrollToTop } from 'common/hooks/scroll';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import { useContactForm } from '../../hooks/form';
import { getChannelSelectionAvailability } from '../../utils/channel';
import {
  getSellerContactSlices,
  getSlicesWithChannelFields,
  overrideExistingSlice,
  removeChannelSelectionSlice,
} from '../../utils/slices';
import ChannelFields from '../ChannelSelection/ChannelFields';
import { FieldName } from '../enums';
import FormGroupWithSlots from '../Fields/FormGroupWithSlots';
import { headlineStyles } from '../Headline';
import type { ContactFormFieldSlice, Settings, UserData } from '../types';
import AlertMessage from './AlertMessage';
import HelpSectionLinks from './common/HelpSectionLinks';

interface FormProps {
  isLoggedIn: boolean;
  slices: ContactFormFieldSlice[];
  reason: string;
  userData: Nullable<UserData>;
  settings: Settings;
  isChannelSelectionActive: boolean;
  setSlices: DispatchState<ContactFormFieldSlice[]>;
  setIsChannelSelectionActive: DispatchState<boolean>;
  skuFromUrl: Nullable<string>;
  orderIdFromUrl: Nullable<string>;
  sellerName: Nullable<string>;
}

const UnstyledNextButton = styled(Button)<WidthProps>`
  ${width}
`;

const NextButton = (props: ComponentProps<typeof UnstyledNextButton>) => (
  <UnstyledNextButton width={1} {...props} />
);

export const Form = ({
  isLoggedIn,
  slices,
  reason,
  userData,
  settings,
  isChannelSelectionActive,
  skuFromUrl,
  orderIdFromUrl,
  sellerName,
  setSlices,
  setIsChannelSelectionActive,
}: FormProps) => {
  const { t } = useLocalization();
  const [selectedChannel, setSelectedChannel] = useState<Nullable<Channel>>(null);
  const { scrollToTop } = useScrollToTop();
  const isSellerContactMode = !!sellerName;
  const processedSlices = isSellerContactMode
    ? getSellerContactSlices(slices)
    : removeChannelSelectionSlice(slices);
  const isChannelSelectionAvailable = getChannelSelectionAvailability(slices, settings);
  const {
    formGroups,
    isFormInvalid,
    isLoading,
    isSuccess,
    formSubmitError,
    submitForm,
    ...restProps
  } = useContactForm({
    slices: processedSlices,
    isLoggedIn,
    reason,
    userData,
    settings,
    selectedChannel,
    isChannelSelectionActive,
    skuFromUrl,
    orderIdFromUrl,
    sellerName,
  });
  const onSelectChannel = (channelOption: Channel) => {
    setSelectedChannel(channelOption);

    setSlices(
      overrideExistingSlice({
        slices,
        fieldName: FieldName.Phone,
        overrides: { required: channelOption !== Channel.Email },
      })
    );
  };
  const onClickNextButton = () => {
    scrollToTop();

    setIsChannelSelectionActive(true);
    setSlices(getSlicesWithChannelFields(slices));
  };
  const submitButton = (
    <FormSubmitButton
      defaultButtonLabel={t(isSellerContactMode ? 'h24_proceed' : 'h24_contact_form_submit')}
      successButtonLabel={t('h24_contact_form_submit_success')}
      isDisabled={isFormInvalid || isSuccess}
      isLoading={isLoading}
      isSuccess={isSuccess}
    />
  );

  return (
    <form noValidate aria-label={t('h24_contact_form_label')} onSubmit={submitForm}>
      {isChannelSelectionActive ? (
        <ChannelFields
          formGroups={formGroups}
          submitButton={submitButton}
          settings={settings}
          onSelectChannel={onSelectChannel}
          {...restProps}
        />
      ) : (
        <>
          {!isSellerContactMode && (
            <>
              <HelpSectionLinks reason={reason} />
              <AlertMessage reason={reason} />
            </>
          )}

          {formSubmitError && (
            <Alert mb="sp_16" type={MessageType.Error} dataTestId="form-submit-alert">
              {t('h24_something_went_wrong')}
            </Alert>
          )}

          {isSellerContactMode && orderIdFromUrl && (
            <BodyRegularBR1 mb="sp_16">
              {t('h24_order_number')}: {orderIdFromUrl}
            </BodyRegularBR1>
          )}

          {!isSellerContactMode && (
            <HeadlineH5 {...headlineStyles}>{t('h24_contact_form_heading')}</HeadlineH5>
          )}

          {formGroups.map(formGroup => (
            <FormGroupWithSlots key={formGroup.name} formGroup={formGroup} />
          ))}

          {isChannelSelectionAvailable && !isSellerContactMode ? (
            <NextButton disabled={isFormInvalid || isSuccess} onClick={onClickNextButton}>
              <HeadlineH8 as="span" color="inherit">
                {t('h24_continue')}
              </HeadlineH8>
            </NextButton>
          ) : (
            submitButton
          )}
        </>
      )}
    </form>
  );
};
